import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

// Polygonal Lasso — click to drop straight-edge vertices; close with
// double-click, by clicking near the first vertex, or by host-wired
// commit() (Enter key).
//
// Behaviors:
//   - Pointer down: drop a vertex at the click position. The very first
//     pointer down captures the modifier state used to combine the eventual
//     selection against the existing one (matches MarqueeTool conventions).
//   - Pointer move (with at least one vertex placed): emit a transient
//     preview selection treating the path as closed via a straight line
//     from the last vertex through the current cursor back to the first.
//     If shift is held during the move, the preview edge from the last
//     vertex to the cursor is constrained to a 45° angle.
//   - Double-click pointer down: drop the vertex, then commit the polygon.
//   - Pointer down near the first vertex (within 5 doc-px) when the polygon
//     already has at least 3 vertices: commit the polygon.
//   - commit() (host: Enter key): close and emit a real SetSelectionCommand.
//   - cancel() (host: Escape): restore the original selection transiently
//     and drop the in-progress polygon.
//
// No DOM access — Escape/Enter handling are wired by the host via
// cancel()/commit().

export type CombineMode = "replace" | "add" | "subtract" | "intersect";

export interface PolygonalLassoToolOptions {
  readonly id: string;
  readonly shortcut: string;
}

interface DocPoint {
  x: number;
  y: number;
}

interface PolyState {
  readonly originalSelection: Selection | null;
  readonly combine: CombineMode;
  readonly docW: number;
  readonly docH: number;
  readonly points: DocPoint[];
}

const CLOSE_THRESHOLD_DOC_PX = 5;
const DOUBLE_CLICK_MS = 350;

export class PolygonalLassoTool implements Tool {
  readonly id: string;
  readonly shortcut: string;

  /**
   * Rubber-band a live preview edge between clicks (audit i62) — the host's
   * generic hover-move routing (wantsHoverMove) opts tools into pointermove
   * events even while no drag is in progress by checking this flag.
   */
  readonly tracksHover = true;

  /** Feather radius in doc pixels, applied to the committed selection (audit i67). */
  feather = 0;
  /** Whether to anti-alias the selection edges (audit i159). */
  antiAlias = true;
  /**
   * Explicit combine-mode override set by the options bar's New/Add/
   * Subtract/Intersect button strip (ToolUX's wireCombineModeButtons,
   * audit i60/i162). When set to "add"/"subtract"/"intersect" it wins over
   * the shift/alt-derived default computed at the first pointer-down of a
   * polygon; "new" (or unset) falls back to the existing shift/alt
   * modifier behavior.
   */
  combineMode?: "new" | "add" | "subtract" | "intersect";

  private state: PolyState | null = null;
  private lastDownTime: number | null = null;

  constructor(
    opts: PolygonalLassoToolOptions = { id: "lasso-poly", shortcut: "L" },
  ) {
    this.id = opts.id;
    this.shortcut = opts.shortcut;
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);

    // Starting a fresh polygon.
    if (!this.state) {
      this.state = {
        originalSelection: ctx.doc.selection,
        combine: pickCombine(s.shiftKey, s.altKey, this.combineMode),
        docW: ctx.doc.width,
        docH: ctx.doc.height,
        points: [{ x: p.x, y: p.y }],
      };
      this.lastDownTime = s.timeStamp;
      return;
    }

    const st = this.state;
    // If the click lands near the first vertex (and we have at least 3 verts
    // already), close the polygon.
    if (st.points.length >= 3) {
      const first = st.points[0]!;
      const ddx = p.x - first.x;
      const ddy = p.y - first.y;
      if (
        ddx * ddx + ddy * ddy <=
        CLOSE_THRESHOLD_DOC_PX * CLOSE_THRESHOLD_DOC_PX
      ) {
        this.commit(ctx);
        return;
      }
    }

    // Detect double-click: same-tool pointer-down within DOUBLE_CLICK_MS of
    // the previous one closes the polygon after this vertex is added.
    const isDoubleClick =
      this.lastDownTime !== null &&
      s.timeStamp - this.lastDownTime <= DOUBLE_CLICK_MS;
    this.lastDownTime = s.timeStamp;

    // Otherwise: add this vertex.
    st.points.push({ x: p.x, y: p.y });

    if (isDoubleClick && st.points.length >= 3) {
      this.commit(ctx);
    }
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.state) return;
    const st = this.state;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const last = st.points[st.points.length - 1]!;
    // Apply 45-degree constraint if shift is held.
    const cursor = s.shiftKey ? constrain45(last, p) : p;
    // Build a preview polygon = existing vertices + the cursor as a temp tail.
    // Selection.fromPolygon treats the polygon as implicitly closed, so the
    // closing edge from cursor → first vertex is automatic.
    const previewPoints = st.points.concat([cursor]);
    const preview = this.buildSelection(st, previewPoints);
    ctx.emitTransient(new SetSelectionCommand(preview));
  }

  // No-op: vertex placement happens on pointer-down; pointer-up is informational
  // for click-based tools. We keep the method present for tool-interface
  // symmetry but intentionally don't touch state.
  onPointerUp(_ctx: ToolContext, _s: PointerSample): void {
    // intentionally empty
  }

  /** Host-wired: Enter key closes and commits the polygon. */
  commit(ctx: ToolContext): void {
    if (!this.state) return;
    const st = this.state;
    this.state = null;
    this.lastDownTime = null;
    if (st.points.length < 3) {
      // Not enough points to form a polygon — restore original.
      ctx.emit(new SetSelectionCommand(st.originalSelection));
      return;
    }
    const next = this.buildSelection(st, st.points);
    // Apply feather at commit time (matches MarqueeTool/LassoTool's convention).
    const feathered = next && this.feather > 0 ? next.feather(this.feather) : next;
    ctx.emit(new SetSelectionCommand(feathered));
  }

  /** Remove the last anchor point (Backspace during polygonal lasso). */
  removeLastPoint(): void {
    if (!this.state) return;
    if (this.state.points.length > 1) {
      this.state.points.pop();
    }
  }

  /** Host-wired: Escape key cancels and discards any in-progress polygon. */
  cancel(ctx: ToolContext): void {
    if (!this.state) return;
    const original = this.state.originalSelection;
    this.state = null;
    this.lastDownTime = null;
    ctx.emitTransient(new SetSelectionCommand(original));
  }

  private buildSelection(
    st: PolyState,
    points: ReadonlyArray<DocPoint>,
  ): Selection | null {
    if (points.length < 3) {
      // Too small to rasterize; preview is the original selection.
      return st.originalSelection;
    }
    const local = Selection.fromPolygon(st.docW, st.docH, points, this.antiAlias);
    const original = st.originalSelection;
    switch (st.combine) {
      case "replace":
        return local.isEmpty() ? null : local;
      case "add":
        if (!original) return local.isEmpty() ? null : local;
        if (local.isEmpty()) return original;
        return original.union(local);
      case "subtract":
        if (!original) return null;
        if (local.isEmpty()) return original;
        return original.subtract(local);
      case "intersect":
        if (!original) return null;
        if (local.isEmpty()) return null;
        return original.intersect(local);
    }
  }
}

function pickCombine(shift: boolean, alt: boolean, override?: string): CombineMode {
  // An explicit options-bar combine-mode selection (audit i60/i162) wins over
  // the shift/alt-derived default. "new" (or no override) falls through to
  // the existing modifier-key behavior below.
  if (override === "add" || override === "subtract" || override === "intersect") {
    return override;
  }
  if (shift && alt) return "intersect";
  if (shift) return "add";
  if (alt) return "subtract";
  return "replace";
}

// Snap the vector (cursor - anchor) to the nearest 45° angle, preserving its
// length. Used for shift-constrained edge preview.
function constrain45(anchor: DocPoint, cursor: DocPoint): DocPoint {
  const dx = cursor.x - anchor.x;
  const dy = cursor.y - anchor.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return { x: cursor.x, y: cursor.y };
  // Quantize the direction angle to the nearest 45° (PI/4) step.
  const ang = Math.atan2(dy, dx);
  const step = Math.PI / 4;
  const qa = Math.round(ang / step) * step;
  return {
    x: anchor.x + Math.cos(qa) * len,
    y: anchor.y + Math.sin(qa) * len,
  };
}
